import React from 'react';
import { Link } from 'react-router-dom';
import { Star, Shield, Award, Package } from 'lucide-react';

// Actual Hing (Asafoetida) Images - Light golden brown color
const HING_IMAGES = {
  bowl: "https://static.prod-images.emergentagent.com/jobs/ff8af06a-976d-4efd-824f-28da774a6e41/images/cd9f10a94af263e218aa96fcc41f3053bda0e23a8389e81b98cc024b18cc0495.png",
  jar: "https://static.prod-images.emergentagent.com/jobs/ff8af06a-976d-4efd-824f-28da774a6e41/images/6697fe7a497ce8a657d75368e4134fd66434b1ee1b69d14f3d1f52fb12bb2a00.png",
  cooking: "https://static.prod-images.emergentagent.com/jobs/ff8af06a-976d-4efd-824f-28da774a6e41/images/8d3d34df708a7f5de29bc09f393fd5b8a6f149ebadab7f4939aa86ad903833bc.png",
  premium: "https://static.prod-images.emergentagent.com/jobs/ff8af06a-976d-4efd-824f-28da774a6e41/images/6164d72ca30ff9a1befa3d1b9d07e77b0d219421a169047d10020919d2b5b5f3.png",
};

const ProductsPage = () => {
  const products = [
    {
      id: 1,
      name: "Premium Hing Powder",
      description: "Our flagship product - finest quality asafoetida powder with rich aroma and authentic taste. Perfect for dals, vegetables, and curries.",
      weights: ["50g", "100g", "200g"],
      features: ["100% Pure", "Strong Aroma", "Lab Tested"],
      image: HING_IMAGES.bowl,
      badge: "Bestseller"
    },
    {
      id: 2,
      name: "Super Hing",
      description: "Extra concentrated formula for that perfect tadka. A little goes a long way - ideal for professional cooks and restaurants.",
      weights: ["25g", "50g", "100g"],
      features: ["Extra Strong", "Premium Grade", "Professional Choice"],
      image: HING_IMAGES.jar,
      badge: "Premium"
    },
    {
      id: 3,
      name: "Hing Granules",
      description: "Easy-to-use granulated asafoetida. Perfect for everyday cooking with convenient dosing and longer shelf life.",
      weights: ["100g", "200g", "500g"],
      features: ["Easy to Use", "Long Shelf Life", "Family Pack"],
      image: HING_IMAGES.cooking,
      badge: "Family Pack"
    },
    {
      id: 4,
      name: "Hing Tablets",
      description: "Pre-measured tablets for consistent flavor in every dish. No mess, no waste - just perfect results every time.",
      weights: ["30 Tablets", "60 Tablets"],
      features: ["Pre-measured", "Convenient", "No Waste"],
      image: HING_IMAGES.premium,
      badge: "New"
    }
  ];

  const usages = [
    {
      title: "Dal Tadka",
      description: "Add a pinch of Hing to hot oil for that authentic smoky flavor in your dals"
    },
    {
      title: "Vegetable Curries",
      description: "Enhance the taste of your sabzis with our premium Hing"
    },
    {
      title: "Pickles & Chutneys",
      description: "Essential ingredient for authentic Indian pickles and preserves"
    },
    {
      title: "Digestive Aid",
      description: "Traditional use as a digestive and anti-flatulent remedy"
    }
  ];

  return (
    <div data-testid="products-page">
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-orange-600 to-orange-800 text-white py-24" data-testid="products-hero">
        <div className="max-w-7xl mx-auto px-4 md:px-8 text-center">
          <h1 className="font-display text-4xl md:text-5xl font-bold mb-6">
            Our Premium Hing Range
          </h1>
          <p className="text-xl text-white/90 max-w-2xl mx-auto">
            Discover our carefully curated selection of pure, premium-quality 
            Asafoetida products for authentic Indian cooking
          </p>
        </div>
      </section>

      {/* Products Grid */}
      <section className="py-20 bg-white" data-testid="products-grid">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="grid md:grid-cols-2 gap-10">
            {products.map((product) => (
              <div 
                key={product.id}
                className="bg-white border border-gray-100 rounded-2xl overflow-hidden shadow-sm hover:shadow-xl transition-all duration-300"
                data-testid={`product-${product.id}`}
              >
                <div className="grid md:grid-cols-2">
                  <div className="h-64 md:h-full overflow-hidden bg-gradient-to-br from-orange-50 to-orange-100 relative">
                    <img 
                      src={product.image} 
                      alt={product.name}
                      className="w-full h-full object-cover"
                    />
                    <span className="absolute top-4 left-4 bg-brand-orange text-white px-3 py-1 rounded-full text-sm font-semibold">
                      {product.badge}
                    </span>
                  </div>
                  <div className="p-8">
                    <h3 className="text-2xl font-semibold mb-3">{product.name}</h3>
                    <p className="text-gray-600 text-sm mb-6 leading-relaxed">
                      {product.description}
                    </p>
                    
                    <div className="mb-6">
                      <p className="text-sm text-gray-500 mb-2">Available Sizes:</p>
                      <div className="flex flex-wrap gap-2">
                        {product.weights.map((weight, idx) => (
                          <span 
                            key={idx}
                            className="bg-orange-100 text-brand-orange px-3 py-1 rounded-full text-sm font-medium"
                          >
                            {weight}
                          </span>
                        ))}
                      </div>
                    </div>
                    
                    <div className="space-y-2">
                      {product.features.map((feature, idx) => (
                        <div key={idx} className="flex items-center gap-2 text-sm text-gray-600">
                          <Star className="w-4 h-4 text-yellow-500" />
                          <span>{feature}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Usage Guide */}
      <section className="py-20 bg-brand-cream" data-testid="usage-guide">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="text-center mb-16">
            <h2 className="section-title">How to Use Hing</h2>
            <p className="section-subtitle">
              Unlock the full potential of our premium Hing in your cooking
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {usages.map((usage, index) => (
              <div 
                key={index}
                className="bg-white p-6 rounded-xl shadow-sm hover:shadow-md transition-all"
                data-testid={`usage-${index}`}
              >
                <div className="w-12 h-12 rounded-full bg-orange-100 flex items-center justify-center text-brand-orange font-bold text-xl mb-4">
                  {index + 1}
                </div>
                <h3 className="text-lg font-semibold mb-2">{usage.title}</h3>
                <p className="text-gray-600 text-sm">{usage.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Quality Assurance */}
      <section className="py-20 bg-gray-900 text-white" data-testid="quality-assurance">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="grid md:grid-cols-3 gap-8 text-center">
            <div className="p-8">
              <Shield className="w-16 h-16 mx-auto text-orange-400 mb-6" />
              <h3 className="text-xl font-semibold mb-4">100% Pure</h3>
              <p className="text-gray-400">
                No artificial additives, colors, or preservatives. 
                Just pure, authentic asafoetida.
              </p>
            </div>
            <div className="p-8">
              <Award className="w-16 h-16 mx-auto text-orange-400 mb-6" />
              <h3 className="text-xl font-semibold mb-4">Quality Tested</h3>
              <p className="text-gray-400">
                Every batch undergoes rigorous quality testing 
                to ensure consistent purity and aroma.
              </p>
            </div>
            <div className="p-8">
              <Package className="w-16 h-16 mx-auto text-orange-400 mb-6" />
              <h3 className="text-xl font-semibold mb-4">Sealed Fresh</h3>
              <p className="text-gray-400">
                Air-tight packaging preserves freshness 
                and aroma until the last use.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Inquiry CTA */}
      <section className="py-16 bg-brand-orange" data-testid="products-cta">
        <div className="max-w-4xl mx-auto px-4 md:px-8 text-center text-white">
          <h2 className="font-display text-3xl md:text-4xl font-bold mb-6">
            Interested in Our Products?
          </h2>
          <p className="text-xl text-white/90 mb-8">
            Contact us for bulk orders, distribution partnerships, or product inquiries
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <Link to="/contact" className="btn-outline" data-testid="contact-btn">
              Contact Us
            </Link>
            <Link to="/quality" className="btn-outline" data-testid="quality-btn">
              View Quality Standards
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ProductsPage;
